import { ElementType, Payrails, PayrailsEnvironment, type InitOptions } from '@payrails/web-sdk';
import { HttpClient } from '@/shared/lib/http-client';
import type { PayrailsTheme } from '../types';

type PayrailsClient = ReturnType<typeof Payrails.init>;
type CollectContainer = ReturnType<PayrailsClient['collectContainer']>;
type SecureField = ReturnType<CollectContainer['createCollectElement']>;

export const PAYRAILS_FIELD_IDS = {
  cardNumber: 'payrails-card-number',
  expiry: 'payrails-card-expiry',
  cvc: 'payrails-card-cvc',
} as const;

export type PayrailsFieldKey = keyof typeof PAYRAILS_FIELD_IDS;

export interface PayrailsFieldState {
  focused: boolean;
  empty: boolean;
  complete: boolean;
}

export type PayrailsFieldStateListener = (
  field: PayrailsFieldKey,
  state: PayrailsFieldState,
) => void;

type CollectFieldState = { isEmpty?: boolean; isValid?: boolean; isComplete?: boolean };

function fieldStateComplete(s: CollectFieldState): boolean {
  if (typeof s.isComplete === 'boolean') return s.isComplete;
  return !s.isEmpty && Boolean(s.isValid);
}

class PayrailsProvider extends HttpClient {
  private payrailsClient: PayrailsClient | null = null;
  private container: CollectContainer | null = null;
  private fields: Array<{ key: PayrailsFieldKey; field: SecureField }> = [];
  private warmPromise: Promise<void> | null = null;
  private initPromise: Promise<void> | null = null;

  constructor(baseURL: string) {
    super({ baseURL, headers: { 'Content-Type': 'text/plain' } });
  }

  async warm(): Promise<void> {
    if (this.payrailsClient) return;
    if (this.warmPromise) return this.warmPromise;

    this.warmPromise = (async () => {
      const initOptions = await this.post<InitOptions>('/init/session', '');
      this.payrailsClient = Payrails.init(initOptions, {
        environment: PayrailsEnvironment.PRODUCTION,
      });
    })();

    try {
      await this.warmPromise;
    } catch (err) {
      this.warmPromise = null;
      throw err;
    }
  }

  async init(
    theme: PayrailsTheme = 'light',
    onReady?: () => void,
    onFieldState?: PayrailsFieldStateListener,
  ): Promise<void> {
    if (this.container) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      await this.warm();
      if (!this.payrailsClient) throw new Error('Payrails client missing after warm');

      const FONT = '-apple-system, BlinkMacSystemFont, system-ui, sans-serif';

      const lightInput = {
        base: {
          width: '100%',
          height: '52px',
          boxSizing: 'border-box',
          padding: '0 16px',
          fontFamily: FONT,
          fontSize: '16px',
          fontWeight: '400',
          color: '#1a1613',
          background: '#FFFFFF',
          outline: 'none',
          '&::placeholder': { color: '#c0b7ae' },
        },
        empty: { color: '#c0b7ae' },
        complete: { color: '#1a1613' },
        invalid: { color: '#d6493a' },
        cardIcon: { display: 'none' },
      };

      const defaultInput = {
        base: {
          width: '100%',
          height: '52px',
          boxSizing: 'border-box',
          padding: '0 16px',
          fontFamily: FONT,
          fontSize: '16px',
          fontWeight: '500',
          color: '#0F1924',
          background: '#FFFFFF',
          border: '1px solid #E2E2E5',
          borderRadius: '8px',
          outline: 'none',
        },
        focus: { border: '1px solid #18181B', boxShadow: '0 0 0 3px rgba(24,24,27,0.08)' },
        empty: { color: '#9A9AA0' },
        complete: { color: '#0F1924' },
        invalid: {
          border: '1px solid #DC2626',
          color: '#DC2626',
          boxShadow: '0 0 0 3px rgba(220, 38, 38, 0.10)',
        },
        cardIcon: { opacity: '0.7' },
      };

      const inputStyles = theme === 'light' ? lightInput : defaultInput;
      const errorTextStyles = { base: { display: 'none' } };

      const container = this.payrailsClient.collectContainer({ containerType: 'COLLECT' });

      const specs = [
        { key: 'cardNumber', type: ElementType.CARD_NUMBER, placeholder: 'XXXX XXXX XXXX XXXX', id: PAYRAILS_FIELD_IDS.cardNumber },
        { key: 'expiry', type: ElementType.EXPIRATION_DATE, placeholder: 'MM / YY', id: PAYRAILS_FIELD_IDS.expiry },
        { key: 'cvc', type: ElementType.CVV, placeholder: '•••', id: PAYRAILS_FIELD_IDS.cvc },
      ] as const;

      let readyCount = 0;
      this.fields = specs.map(({ key, type, placeholder, id }) => {
        const field = container.createCollectElement({
          type,
          placeholder,
          required: true,
          enableCardIcon: false,
          inputStyles,
          errorTextStyles,
        });
        let focused = false;
        const emit = () => {
          const s = field.getState?.();
          const empty = s ? s.isEmpty : true;
          const complete = s ? fieldStateComplete(s) : false;
          onFieldState?.(key, { focused, empty, complete });
        };
        field.on('READY', () => {
          readyCount += 1;
          if (readyCount === specs.length) onReady?.();
        });
        field.on('FOCUS', () => {
          focused = true;
          emit();
        });
        field.on('BLUR', () => {
          focused = false;
          emit();
        });
        field.on('CHANGE', () => emit());
        if (!field.mount) throw new Error('Payrails secure field lacks mount()');
        field.mount(`#${id}`);
        return { key, field };
      });

      this.container = container;
    })();

    try {
      await this.initPromise;
    } catch (err) {
      this.initPromise = null;
      throw err;
    }
  }

  async tokenize() {
    if (!this.container) {
      throw new Error('Payment form is still loading. Please wait.');
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any = await this.container.tokenize({ storeInstrument: true });
    if (!result || result.error) {
      throw new Error(result?.error || 'Tokenization failed');
    }
    const instrument = result.instrument ?? result;
    if (!instrument?.id) throw new Error('No instrument returned from Payrails');
    return instrument as { id: string; paymentMethod?: string; data?: { network?: string } };
  }

  validateFields(): Record<PayrailsFieldKey, boolean> {
    const result: Record<PayrailsFieldKey, boolean> = {
      cardNumber: false,
      expiry: false,
      cvc: false,
    };
    for (const { key, field } of this.fields) {
      const s = field.getState?.();
      result[key] = s ? fieldStateComplete(s) : false;
    }
    return result;
  }

  reset() {
    for (const { field } of this.fields) {
      try {
        field.unmount?.();
      } catch {
        // best-effort teardown
      }
    }
    this.fields = [];
    this.container = null;
    this.payrailsClient = null;
    this.warmPromise = null;
    this.initPromise = null;
  }
}

export const payrailsProvider = new PayrailsProvider(
  import.meta.env.VITE_PAYMENT_API_URL || '',
);

export const warmPayrails = () => payrailsProvider.warm();
export const initPayrails = (
  theme?: PayrailsTheme,
  onReady?: () => void,
  onFieldState?: PayrailsFieldStateListener,
) => payrailsProvider.init(theme, onReady, onFieldState);
export const tokenizeCard = () => payrailsProvider.tokenize();
export const validateCardFields = () => payrailsProvider.validateFields();
export const resetPayrails = () => payrailsProvider.reset();
